import { apiClient } from './axios';

export interface StartReviewResponse {
  message: string;
  reviewId: string;
}

export type ShiftType = 'morning' | 'evening' | 'day' | 'night';

export const SHIFT_TYPE_OPTIONS: { value: ShiftType; label: string }[] = [
  { value: 'morning', label: 'Morning' },
  { value: 'evening', label: 'Evening' },
  { value: 'day', label: 'Day' },
  { value: 'night', label: 'Night' },
];

export interface AvailabilitySlot {
  date: string;
  shift_type: ShiftType;
  day_name?: string;
}

export interface AvailabilityData {
  confirmed: boolean;
  consented: boolean;
  date_range?: string;
  availability_slots?: AvailabilitySlot[];
}

export interface Discrepancy {
  field: string;
  severity: 'high' | 'medium' | 'low';
  original: string;
  reviewed: string;
  description: string;
  original_value?: unknown;
  reviewed_value?: unknown;
}

export interface CampaignReview {
  id: string;
  campaign_id: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'STOPPED';
  total_calls: number;
  reviewed_calls: number;
  calls_with_issues: number;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  campaign: {
    id: string;
    name: string;
    description?: string;
  };
  call_reviews?: CallReview[];
}

export interface CallReview {
  id: string;
  campaign_review_id: string;
  call_id: string;
  review_status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  enhanced_transcript?: Record<string, unknown>;
  discrepancies?: Discrepancy[];
  original_outcome?: string;
  original_lead_data?: Record<string, unknown>;
  reviewed_lead_data?: Record<string, unknown>;
  reviewed_outcome?: string;
  outcome_matches?: boolean;
  original_availability?: AvailabilityData;
  reviewed_availability?: AvailabilityData;
  availability_matches?: boolean;
  original_outputs?: Record<string, unknown>;
  lead_data_matches?: boolean;
  reviewed_outputs?: Record<string, unknown>;
  original_status?: string;
  reviewed_status?: string;
  discrepancy_details?: Record<string, unknown>;
  ai_analysis?: string;
  confidence_score: number;
  additional_review_data?: Record<string, unknown>;
  review_config_id?: string;
  reviewed_at?: string;
  created_at: string;
  updated_at: string;
  call: {
    id: string;
    phone_number: string;
    created_at: string;
    contact?: {
      id: string;
      name: string;
      phone: string;
    };
  };
}

export interface StartCallReviewResponse {
  message: string;
  callReviewId: string;
}

export const reviewsApi = {
  startCampaignReview: async (
    campaignId: string,
    options?: { unreviewedOnly?: boolean; reviewConfigId?: string },
  ): Promise<StartReviewResponse> => {
    const params = new URLSearchParams();
    if (options?.unreviewedOnly) params.set('unreviewedOnly', 'true');
    if (options?.reviewConfigId) params.set('reviewConfigId', options.reviewConfigId);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.post<StartReviewResponse>(
      `/reviews/campaigns/${campaignId}/start${queryString}`,
    );
    return response.data;
  },

  getCampaignReview: async (reviewId: string): Promise<CampaignReview> => {
    const response = await apiClient.get(`/reviews/campaigns/${reviewId}`);
    return response.data;
  },

  getCampaignReviews: async (campaignId: string): Promise<CampaignReview[]> => {
    const response = await apiClient.get(`/reviews/by-campaign/${campaignId}`);
    return response.data;
  },

  getCallReview: async (callReviewId: string): Promise<CallReview> => {
    const response = await apiClient.get(`/reviews/calls/${callReviewId}`);
    return response.data;
  },

  // Individual call review methods
  startCallReview: async (
    callId: string,
    options?: { reviewConfigId?: string },
  ): Promise<StartCallReviewResponse> => {
    const params = options?.reviewConfigId ? `?reviewConfigId=${options.reviewConfigId}` : '';
    const response = await apiClient.post<StartCallReviewResponse>(
      `/reviews/call/${callId}/start${params}`,
    );
    return response.data;
  },

  getCallReviewsByCallId: async (callId: string): Promise<CallReview[]> => {
    const response = await apiClient.get(`/reviews/by-call/${callId}`);
    return response.data;
  },

  stopCampaignReview: async (reviewId: string): Promise<{ message: string; reviewId: string }> => {
    const response = await apiClient.post(`/reviews/campaigns/${reviewId}/stop`);
    return response.data;
  },

  translateCallTranscript: async (
    callId: string,
    options?: { language?: string; force?: boolean },
  ): Promise<{
    status: 'translated' | 'not_needed' | 'already_translated';
    targetLanguage: string;
    detectedSourceLanguage: string | null;
  }> => {
    const params = new URLSearchParams();
    if (options?.language) params.set('language', options.language);
    if (options?.force) params.set('force', 'true');
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.post(
      `/reviews/call/${callId}/translate-transcript${queryString}`,
    );
    return response.data;
  },
};
